import React, { useEffect, useState } from "react";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function api(path, opts = {}) {
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: "include",
  }).then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

const PAGE_SIZE = 50;

const EVENT_OPTIONS = [
  { value: "", label: "All events" },
  { value: "chat.started", label: "chat.started" },
  { value: "chat.completed", label: "chat.completed" },
  { value: "chat.error", label: "chat.error" },
  // Access-control changes: who granted or revoked what, and when.
  { value: "rbac.grant.user.changed", label: "rbac.grant.user.changed" },
  { value: "rbac.grant.department.changed", label: "rbac.grant.department.changed" },
  { value: "rbac.user.department.changed", label: "rbac.user.department.changed" },
  { value: "rbac.department.created", label: "rbac.department.created" },
  { value: "rbac.department.updated", label: "rbac.department.updated" },
  { value: "rbac.department.deleted", label: "rbac.department.deleted" },
];

// Timestamps read as YYYY-MM-DD HH:MM (UTC) everywhere in this page.
function fmtTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 16).replace("T", " ");
}

// Durations are reported in seconds, not milliseconds.
function fmtSeconds(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return "";
  return `${(Number(ms) / 1000).toFixed(1)} s`;
}

// Run time is the end-to-end interaction: from when the user submitted to when
// the audit row was written. The upstream call latency is within a few ms of
// this, so it isn't shown separately (it stays in the CSV export as
// latency_seconds for anyone reconciling against the workflow's own timing).
function fmtRunTime(row) {
  if (!row.created_at || !row.ended_at) return "";
  return fmtSeconds(new Date(row.ended_at) - new Date(row.created_at));
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export default function AuditLogPage() {
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    user_id: "",
    event: "",
    session_id: "",
  });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  function buildQuery(extra = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.set(k, v);
    });
    return params.toString();
  }

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const qs = buildQuery({ limit: PAGE_SIZE, offset });
      const json = await api(`/compliance/audit?${qs}`);
      setData(json);
    } catch (e) {
      setErr(e.message);
      setData({ rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }

  // Live-fetch when filters or offset change, debounced so typing in
  // user_id / session_id doesn't hammer the API.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, offset]);

  // Update a single filter and reset to page 0 so we don't end up on a
  // stale offset for a freshly-narrowed result set.
  function setFilter(field, value) {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setOffset(0);
  }

  function clearFilters() {
    setFilters({ from: "", to: "", user_id: "", event: "", session_id: "" });
    setOffset(0);
  }

  function exportCsv() {
    const qs = buildQuery();
    // Browser navigation doesn't carry our Authorization header, so use
    // fetch + Blob download to keep auth on the request.
    fetch(`/api/compliance/audit/export.csv${qs ? `?${qs}` : ""}`, {
      headers: { ...authHeaders() },
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `aegis_audit_${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((e) => setErr(`Export failed: ${e.message}`));
  }

  const rows = data.rows || [];
  const total = data.total || 0;
  const showingFrom = rows.length === 0 ? 0 : offset + 1;
  const showingTo = offset + rows.length;
  const canPrev = offset > 0;
  const canNext = offset + rows.length < total;

  const eventBadgeStyle = (ev) => {
    const base = {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 600,
    };
    if (ev === "chat.completed") return { ...base, background: "#d1f7dc", color: "#13651f" };
    if (ev === "chat.error") return { ...base, background: "#fde2e1", color: "#a31919" };
    if (ev === "chat.started") return { ...base, background: "#e1ecfb", color: "#0c4ea2" };
    // Access-control changes get their own colour so they stand out from
    // chat traffic when scanning an unfiltered log.
    if (ev && ev.startsWith("rbac.")) return { ...base, background: "#f3e6ff", color: "#5b21b6" };
    return { ...base, background: "#eee", color: "#333" };
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 4 }}>Audit Log</h2>
      <div style={{ color: "#555", marginBottom: 16, fontSize: 14 }}>
        Append-only record of every chat interaction: started, completed, or errored.
        Rows cannot be modified or deleted.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "end", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          From
          <input
            type="date"
            value={filters.from}
            onChange={(e) => setFilter("from", e.target.value)}
            style={{ padding: 6 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          To
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilter("to", e.target.value)}
            style={{ padding: 6 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          User ID
          <input
            type="number"
            value={filters.user_id}
            onChange={(e) => setFilter("user_id", e.target.value)}
            placeholder="any"
            style={{ padding: 6, width: 90 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12 }}>
          Event
          <select
            value={filters.event}
            onChange={(e) => setFilter("event", e.target.value)}
            style={{ padding: 6 }}
          >
            {EVENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12, flex: 1, minWidth: 220 }}>
          Session ID
          <input
            type="text"
            value={filters.session_id}
            onChange={(e) => setFilter("session_id", e.target.value)}
            placeholder="UUID"
            style={{ padding: 6 }}
          />
        </label>
        <button type="button" onClick={clearFilters} className="btn btn-secondary" style={{ padding: "6px 12px" }}>
          Clear Filters
        </button>
        <button type="button" onClick={exportCsv} className="btn btn-success" style={{ padding: "6px 12px" }}>
          Export CSV
        </button>
      </div>

      {err && (
        <div style={{ background: "#fde2e1", color: "#a31919", padding: 8, borderRadius: 4, marginBottom: 12 }}>
          {err}
        </div>
      )}

      <div style={{ marginBottom: 8, fontSize: 13, color: "#555" }}>
        {loading ? "Loading…" : `Showing ${showingFrom}–${showingTo} of ${total}`}
        <span style={{ marginLeft: 12, fontStyle: "italic" }}>
          · Click any row to see details
        </span>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f5f5f5" }}>
            <tr>
              <th style={th}>ID</th>
              <th style={th}>Time (UTC)</th>
              <th style={th}>User</th>
              <th style={th}>Event</th>
              <th style={th}>Chat</th>
              <th style={th}>Status</th>
              <th style={th}>Run Time</th>
              <th style={th}>Query</th>
              <th style={th}>Docs</th>
              <th style={th}>Session</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={10} style={{ padding: 12, textAlign: "center", color: "#888" }}>
                  No rows.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isOpen = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                    style={{ cursor: "pointer", borderTop: "1px solid #eee" }}
                  >
                    <td style={td}>{r.id}</td>
                    <td style={td}>{fmtTs(r.created_at)}</td>
                    <td style={td}>{r.user_email || r.user_id || "—"}</td>
                    <td style={td}>
                      <span style={eventBadgeStyle(r.event)}>{r.event}</span>
                    </td>
                    <td style={td}>{r.surface || "—"}</td>
                    <td style={td}>{r.status_code ?? ""}</td>
                    <td style={td}>{fmtRunTime(r)}</td>
                    <td style={td} title={r.query_text || ""}>{truncate(r.query_text, 60)}</td>
                    <td style={td}>{(r.documents || []).length || ""}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 11 }}>
                      {r.session_id ? r.session_id.slice(0, 8) : ""}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={10} style={{ padding: 0 }}>
                        <div style={detailCard}>
                          <DetailGrid row={r} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
        <button
          type="button"
          disabled={!canPrev || loading}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          className="btn btn-secondary"
        >
          ← Previous
        </button>
        <button
          type="button"
          disabled={!canNext || loading}
          onClick={() => setOffset(offset + PAGE_SIZE)}
          className="btn btn-secondary"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function DetailGrid({ row }) {
  const Field = ({ label, children, mono = true, wide = false }) => (
    <div style={{ marginBottom: 12, ...(wide ? { gridColumn: "1 / -1" } : null) }}>
      <div style={detailLabel}>{label}</div>
      <div
        style={{
          whiteSpace: "pre-wrap",
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: 12,
          color: "#222",
        }}
      >
        {(children == null || children === "") ? <span style={{ color: "#999" }}>—</span> : children}
      </div>
    </div>
  );

  // Pretty-print retrieval_rows JSON; warn if huge
  let retrievalDisplay = null;
  if (row.retrieval_rows != null) {
    try {
      const txt = JSON.stringify(row.retrieval_rows, null, 2);
      retrievalDisplay = (
        <pre style={preBlock}>{txt}</pre>
      );
    } catch {
      retrievalDisplay = String(row.retrieval_rows);
    }
  }

  // SQL queries get the same code-block treatment as JSON
  const sqlDisplay = row.sql_query ? (
    <pre style={preBlock}>{row.sql_query}</pre>
  ) : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <Field label="Query" wide>{row.query_text}</Field>
      <Field label={row.event === "chat.error" ? "Error" : "Response"} wide>{row.response_text}</Field>

      <Field label="Session">{row.session_id}</Field>
      <Field label="Document Name(s) and Version">
        {(row.documents || [])
          .map((d) => (d.version ? `${d.document_number} v${d.version}` : d.document_number))
          .join(", ")}
      </Field>

      <Field label="Started">{fmtTs(row.created_at)}</Field>
      <Field label="Ended">{fmtTs(row.ended_at)}</Field>

      <Field label="Run Time">{fmtRunTime(row)}</Field>
      <Field label="Status">{row.status_code}</Field>

      <Field label="Chat">{row.surface}</Field>
      <Field label="Model">{row.model_name}</Field>
      <Field label="Temperature">{row.model_temperature}</Field>

      <Field label="Tokens">
        {row.tokens_in != null || row.tokens_out != null
          ? `in=${row.tokens_in ?? "?"}  out=${row.tokens_out ?? "?"}`
          : ""}
      </Field>
      <Field label="Retrieval rows">{row.retrieval_row_count}</Field>

      <Field label="SQL query" wide mono={false}>{sqlDisplay}</Field>

      <Field label="Retrieval rows (data)" wide mono={false}>{retrievalDisplay}</Field>

      <Field label="System prompt hash">{row.system_prompt_hash}</Field>
      <Field label="Response hash">{row.response_hash}</Field>
      <Field label="Bundle hash" wide>{row.bundle_hash}</Field>
      <Field label="Previous bundle hash (chain)" wide>{row.previous_hash}</Field>
    </div>
  );
}

const th = { padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, textTransform: "uppercase", color: "#444" };
const td = { padding: "8px 10px", verticalAlign: "top" };

// Light "card" inside the expanded row so the detail panel stays legible
// regardless of the parent page theme (light or dark).
const detailCard = {
  background: "#ffffff",
  color: "#222",
  padding: 16,
  borderTop: "2px solid #4a90e2",
  borderBottom: "1px solid #ddd",
};

const detailLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 4,
};

const preBlock = {
  margin: 0,
  maxHeight: 240,
  overflow: "auto",
  background: "#f5f5f5",
  color: "#222",
  border: "1px solid #ddd",
  borderRadius: 3,
  padding: 8,
  fontSize: 11,
  whiteSpace: "pre",
};
